// Whether a service on an SHA visit may go ahead, as far as pre-authorisation goes
// (docs/plans/phase1-sha.md §6.4 and §7.5; checklist items 20 and 21).
//
// The states here are Wodi's. SHA's own status values reach this module only after the
// adapter has mapped them to a request state, from a table taken from the specification
// (TODO(verify-spec): B6 in the plan). Which services need pre-authorisation comes from
// the official list loaded through the import framework, never from code.

/** What the active pre-authorisation list says about the service. */
export type PreauthRequirement =
  /** The list flags the service for this scheme and facility level. */
  | 'required'
  /** The list is loaded and doesn't flag it. */
  | 'not_required'
  /** No list is loaded, so nobody can tell. Never read as "not required". */
  | 'unknown';

export const PREAUTH_REQUEST_STATES = [
  'queued',
  'submitted',
  'approved',
  'declined',
  'expired',
  'cancelled',
  'send_failed',
] as const;

export type PreauthRequestState = (typeof PREAUTH_REQUEST_STATES)[number];

export interface PreauthRequestFacts {
  readonly id: string;
  readonly state: PreauthRequestState;
  readonly requestedAt: Date;
  /** Until when an approval holds, as SHA returned it; null when SHA gave no end. */
  readonly validUntil: Date | null;
}

export const PREAUTH_GATE_STATES = [
  'not_required',
  'requirement_unknown',
  'needs_request',
  'request_failed',
  'waiting',
  'approved',
  'declined',
] as const;

export type PreauthGateState = (typeof PREAUTH_GATE_STATES)[number];

/** English descriptions; the screens use the shared gate labels. */
export const PREAUTH_GATE_LABELS: Record<PreauthGateState, string> = {
  not_required: 'No SHA approval needed',
  requirement_unknown: 'Pre-authorisation list not loaded',
  needs_request: 'Needs approval from SHA',
  request_failed: 'Couldn’t ask SHA for approval',
  waiting: 'Waiting for SHA approval',
  approved: 'SHA approved',
  declined: 'SHA declined',
};

export interface PreauthGateInput {
  readonly requirement: PreauthRequirement;
  /** Every request for this service on this visit, in any order. */
  readonly requests: readonly PreauthRequestFacts[];
  readonly at: Date;
}

function newestFirst(a: PreauthRequestFacts, b: PreauthRequestFacts): number {
  const byTime = b.requestedAt.getTime() - a.requestedAt.getTime();
  if (byTime !== 0) return byTime;
  return a.id < b.id ? 1 : a.id > b.id ? -1 : 0;
}

function stateOfRequest(request: PreauthRequestFacts, at: Date): PreauthGateState {
  switch (request.state) {
    case 'queued':
    case 'submitted':
      return 'waiting';
    case 'approved':
      // An approval holds until its end, exclusive. After that the service needs asking again.
      return request.validUntil === null || at.getTime() < request.validUntil.getTime()
        ? 'approved'
        : 'needs_request';
    case 'declined':
      return 'declined';
    case 'expired':
      return 'needs_request';
    case 'send_failed':
      return 'request_failed';
    case 'cancelled':
      // Callers never reach this: cancelled requests are skipped.
      return 'needs_request';
  }
}

/**
 * The gate for one service. The newest request that wasn't cancelled decides: asking
 * again after a decline means someone is waiting for a new answer, and an approval that
 * has lapsed needs asking again.
 *
 * When no list is loaded, a request someone made anyway is still honoured (an approval
 * opens the gate, a request in flight waits); otherwise the answer is
 * `requirement_unknown`, and the caller's policy decides what that means.
 */
export function preauthGate(input: PreauthGateInput): PreauthGateState {
  if (Number.isNaN(input.at.getTime())) throw new RangeError('Not a valid instant');
  if (input.requirement === 'not_required') return 'not_required';

  const latest = [...input.requests]
    .filter((r) => r.state !== 'cancelled')
    .sort(newestFirst)
    .at(0);
  const fromRequest = latest === undefined ? null : stateOfRequest(latest, input.at);

  if (input.requirement === 'unknown') {
    return fromRequest === 'approved' || fromRequest === 'waiting'
      ? fromRequest
      : 'requirement_unknown';
  }
  return fromRequest ?? 'needs_request';
}

/**
 * The gate as the screens and the server read it: the pre-authorisation state, or
 * `overridden` when a clinician with `preauth.override` recorded going ahead without an
 * approval SHA hasn't given (plan §7.5 step 4, open question C1).
 */
export const SHA_APPROVAL_STATES = [...PREAUTH_GATE_STATES, 'overridden'] as const;

export type ShaApprovalState = (typeof SHA_APPROVAL_STATES)[number];

/**
 * Applies a recorded override: it counts until SHA approves, the same rule the order rows
 * and the claim use (apps/api/src/sha/preauth-gates.ts). An override can only be recorded
 * while SHA hasn't answered or couldn't be asked; once recorded, a later decline doesn't
 * stop care already under way. An approval, "not required" and "list not loaded" are shown
 * as they are.
 * TODO(clinical-verify): C1 — whether a decline after an override should hold further work.
 */
export function withPreauthOverride(
  state: PreauthGateState,
  overridden: boolean,
): ShaApprovalState {
  if (!overridden) return state;
  return state === 'approved' || state === 'not_required' || state === 'requirement_unknown'
    ? state
    : 'overridden';
}

/**
 * The lab slice's approval answer (phase1-lab.md §4.4), which the gates share.
 * `overridden` lets the work go ahead; the caller audits it with the rule ID.
 */
export type ApprovalGateAnswer =
  'not_applicable' | 'approved' | 'waiting' | 'declined' | 'overridden';

/**
 * What `requirement_unknown` means for the gate. The plan's suggested default (open
 * question P4) is not to hold care back because a list is missing; the claim reports the
 * line instead. A single constant, so the decision is made in one place.
 */
export const PREAUTH_UNKNOWN_REQUIREMENT_ANSWER: ApprovalGateAnswer = 'not_applicable';

export function toApprovalAnswer(
  state: ShaApprovalState,
  unknownRequirement: ApprovalGateAnswer = PREAUTH_UNKNOWN_REQUIREMENT_ANSWER,
): ApprovalGateAnswer {
  switch (state) {
    case 'overridden':
      return 'overridden';
    case 'not_required':
      return 'not_applicable';
    case 'requirement_unknown':
      return unknownRequirement;
    case 'approved':
      return 'approved';
    case 'declined':
      return 'declined';
    case 'needs_request':
    case 'request_failed':
    case 'waiting':
      return 'waiting';
  }
}
